import { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { C } from '../../constants/colors.js';
import { F, N, L } from '../../constants/fonts.js';
import { Btn } from '../ui/Btn.jsx';
import { PB } from '../ui/ProgressBar.jsx';
import { LB } from '../ui/LevelBadge.jsx';
import { FinleyInsightBanner } from '../ui/FinleyInsightBanner.jsx';
import { LoadingDots } from '../ui/LoadingDots.jsx';
import { getInsightMessage } from '../../config/insightMessages.js';
import { getStories } from '../../lib/content-fetch.js';
import { rqConfettiBurst } from '../../lib/animations.js';
import { useTTS } from '../../hooks/useTTS.js';
import { popIn, slideUp, tapBounce, wrongAnswer } from '../../lib/motionPresets.js';

export function StoryGame({theme, tracker, profile, onComplete, onRecord, online, sessionActivityIndex = 0, isPlaylistSession = false}) {
  const [stories, setStories] = useState(null);
  const [si, setSi] = useState(0);
  const [qi, setQi] = useState(0);
  const [sel, setSel] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [insightMessage, setInsightMessage] = useState('');
  const [insightVisible, setInsightVisible] = useState(false);
  const [ttsState, setTtsState] = useState('idle');
  const { speak, stop, speaking } = useTTS();
  const qStart = useRef(Date.now());
  const prevBktRef = useRef(tracker.bkt || {});
  const lv = tracker.currentLevel;

  const stopReading = () => {
    stop();
    setTtsState('idle');
  };

  useEffect(() => () => { stopReading(); }, []);

  const chillMsg = tracker.chillMode ? "You're doing great! Let's review together 🌟" : null;

  useEffect(() => {
    getStories(lv, theme.id, profile, online).then(s => {
      setStories(tracker.chillMode && s?.length > 1 ? s.slice(0, 1) : s);
      qStart.current = Date.now();
    });
  }, []);

  useEffect(() => {
    stopReading();
  }, [si, qi]);

  useEffect(() => {
    const activeTitle = stories?.[si]?.title;
    if (!stories || !activeTitle) return;
    const triggers = [];
    if ((si + qi) > 0 && (si + qi) % 3 === 0) triggers.push('periodic');
    const msg = getInsightMessage({
      activityKey: `stories:${sessionActivityIndex}:${si}:${qi}:${activeTitle}`,
      sessionStart: isPlaylistSession && sessionActivityIndex === 0 && si === 0 && qi === 0,
      triggers
    });
    if (msg) { setInsightMessage(msg); setInsightVisible(true); }
  }, [stories, si, qi, sessionActivityIndex, isPlaylistSession]);

  useEffect(() => {
    const prev = prevBktRef.current || {};
    const next = tracker.bkt || {};
    const crossed = Object.keys(next).some((skill) => {
      const p = prev[skill] || 0;
      const n = next[skill] || 0;
      return (p < 0.6 && n >= 0.6) || (p < 0.85 && n >= 0.85);
    });
    prevBktRef.current = next;
    if (!crossed) return;
    const msg = getInsightMessage({
      activityKey: `stories:milestone:${sessionActivityIndex}:${si}:${qi}`,
      triggers: ['skill_milestone']
    });
    if (msg) { setInsightMessage(msg); setInsightVisible(true); }
  }, [tracker.bkt, si, qi, sessionActivityIndex]);

  useEffect(() => {
    if (!speaking && ttsState === 'speaking') {
      setTtsState('idle');
    }
  }, [speaking, ttsState]);

  if (!stories) return (
    <div style={{textAlign:"center",padding:"40px 24px"}}>
      <div style={{fontSize:56,marginBottom:12,animation:"float 2s ease-in-out infinite"}}>{theme.emoji}</div>
      <LoadingDots msg="Writing your story..."/>
      <p style={{fontFamily:N,fontSize:13,color:C.textLight}}>This takes a few seconds</p>
    </div>
  );

  const story = stories[si];
  const toggleReadAloud = () => {
    try {
      if (!story?.text) return;
      if (ttsState === 'speaking') {
        stopReading();
        return;
      }
      setTtsState('speaking');
      speak(story.text, {
        rate: 0.85,
        pitch: 1.0,
        onEnd: () => {
          setTtsState('idle');
        }
      }).catch(() => {
        setTtsState('idle');
      });
    } catch {
      setTtsState('idle');
    }
  };

  if (done || !story || !story.questions || !story.questions[qi]) return (
    <div style={{textAlign:"center",padding:"36px 24px"}}>
      <div style={{fontSize:64,marginBottom:10,animation:"float 2s ease-in-out infinite"}}>📖</div>
      <h2 style={{fontFamily:F,fontSize:28,color:C.text}}>Done!</h2>
      <p style={{fontFamily:N,fontSize:17,color:C.textMid,margin:"14px 0 20px"}}>{score} stars!</p>
      <Btn onClick={() => onComplete(score)}>Keep Going →</Btn>
    </div>
  );

  const qRaw = story.questions[qi];
  const qAnswer = (qRaw.answer != null && Number.isInteger(qRaw.answer) && qRaw.answer >= 0 && qRaw.answer < (qRaw.options || []).length) ? qRaw.answer : 0;
  const q = {...qRaw, answer: qAnswer};

  return (
    <motion.div {...slideUp} className="relative" style={{padding:"20px 24px"}}>
      <FinleyInsightBanner message={insightMessage} visible={insightVisible} />
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <span style={{fontFamily:N,fontSize:13,color:C.textLight}}>{si+1}/{stories.length}</span>
        <LB level={lv}/>
        <span style={{fontFamily:N,fontSize:13,color:C.primary,fontWeight:700}}>⭐ {score}</span>
      </div>
      <PB current={si} total={stories.length} color={theme.color}/>
      {chillMsg && <p style={{fontFamily:N,fontSize:13,color:C.success,margin:"8px 0 0",textAlign:"center"}}>{chillMsg}</p>}
      {(profile.age <= 7 || lv <= 6) && (
        <div style={{background:"#EBF5FF",borderRadius:14,padding:"10px 14px",margin:"12px 0 0",border:"2px solid #BAD6F2",display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:20,flexShrink:0}}>📖</span>
          <span style={{fontFamily:N,fontSize:13,color:"#1E40AF",lineHeight:1.4}}>Read this story aloud to your child, then let them answer the question below.</span>
          <button onClick={toggleReadAloud} className="rounded-finley-md px-4 py-2 text-sm text-finley-text-light shadow-finley-sm" style={{flexShrink:0,background:ttsState==='speaking'?C.accentLight:'var(--color-finley-bg)',border:ttsState==='speaking'?`2px solid ${C.accent}`:'2px solid #93C5FD',color:"#1E40AF",whiteSpace:"nowrap"}}>
            {ttsState==='speaking'?'⏹ Stop':'🔊 Read to Me'}
          </button>
        </div>
      )}
      <motion.div {...popIn} className="rounded-finley-xl shadow-finley-md" style={{background:theme.colorLight,padding:"20px 18px",margin:"18px 0",border:`2px solid ${theme.color}33`}}>
        <h3 style={{fontFamily:F,fontSize:20,color:theme.color,margin:"0 0 10px"}}>{story.title}</h3>
        <p style={{fontFamily:L,fontSize:(profile.age<=7||lv<=6)?24:lv>=11?16:19,lineHeight:1.8,color:C.text,margin:0}}>{story.text}</p>
      </motion.div>
      <p style={{fontFamily:F,fontSize:17,color:C.text,margin:"0 0 12px",textAlign:"center"}}>{q.question}</p>
      <div style={{display:"flex",flexDirection:"column",gap:8,maxWidth:380,margin:"0 auto"}}>
        {q.options.map((opt, i) => {
          let bg = "#F8F9FA", bdr = "#E5E7EB";
          if (sel !== null) {
            if (i === q.answer) { bg = C.successLight; bdr = C.success; }
            else if (i === sel) { bg = C.dangerLight; bdr = C.danger; }
          }
          return (
            <motion.button key={i} {...tapBounce} animate={sel !== null && i !== q.answer && i === sel ? wrongAnswer.animate : {}} onClick={(e) => {
              if (sel === null) {
                const ms = Date.now() - qStart.current;
                setSel(i);
                if (i === q.answer) { setScore(s => s+2); rqConfettiBurst({count:20,spread:50}); }
                else { e.currentTarget.animate([{transform:'translateX(0)'},{transform:'translateX(-8px)'},{transform:'translateX(8px)'},{transform:'translateX(-6px)'},{transform:'translateX(6px)'},{transform:'translateX(0)'}],{duration:400,easing:'ease-out'}); }
                onRecord("stories", null, i === q.answer, ms);
              }
            }} disabled={sel !== null}
            className="min-h-[48px] rounded-finley-md text-left shadow-finley-sm"
            style={{padding:"13px 16px",fontSize:lv>=11?15:17,fontFamily:N,fontWeight:600,background:bg,border:`2px solid ${bdr}`,borderRadius:14,color:C.text,cursor:sel===null?"pointer":"default",textAlign:"left"}}>
              {opt}
            </motion.button>
          );
        })}
      </div>
      {sel !== null && (
        <motion.div {...popIn} style={{textAlign:"center",marginTop:16}}>
          <p style={{fontFamily:N,fontSize:15,color:C.textMid,margin:"0 0 12px"}}>{sel === q.answer ? "🎉 Correct!" : `Answer: "${q.options[q.answer]}"`}</p>
          <Btn onClick={() => {
            if (qi + 1 < story.questions.length) { setQi(x => x+1); setSel(null); qStart.current = Date.now(); }
            else { if (si + 1 >= stories.length) setDone(true); else { setSi(x => x+1); setQi(0); setSel(null); qStart.current = Date.now(); } }
          }} color={theme.color}>
            {qi+1 < story.questions.length ? "Next Question →" : si+1 >= stories.length ? "Finish ⭐" : "Next →"}
          </Btn>
        </motion.div>
      )}
    </motion.div>
  );
}
